import React, { useMemo, useRef, useState, useEffect } from 'react';

// A Bootstrap dropdown checklist of facet values, each annotated with its
// count. Operators select from real values instead of typing free text.
export default function FacetMultiSelect({ label, options, selected, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const sel = selected || [];
  const opts = options || [];

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) => String(o.value).toLowerCase().includes(q));
  }, [opts, query]);

  const toggle = (value) => {
    if (sel.includes(value)) onChange(sel.filter((v) => v !== value));
    else onChange([...sel, value]);
  };

  const summary =
    sel.length === 0 ? 'Any' : sel.length === 1 ? sel[0] : `${sel.length} selected`;

  return (
    <div className="position-relative" ref={ref}>
      <label className="form-label small mb-1 d-block">{label}</label>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center text-truncate"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || opts.length === 0}
        title={sel.join(', ')}
      >
        <span className="text-truncate">{opts.length === 0 ? 'None available' : summary}</span>
        <span className="ms-2">▾</span>
      </button>
      {open && (
        <div
          className="border rounded bg-white shadow-sm p-2 position-absolute w-100"
          style={{ zIndex: 1050, maxHeight: 280, overflowY: 'auto' }}
        >
          <div className="d-flex gap-1 mb-2">
            <input
              className="form-control form-control-sm"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {sel.length > 0 && (
              <button type="button" className="btn btn-link btn-sm p-0 text-nowrap" onClick={() => onChange([])}>
                Clear
              </button>
            )}
          </div>
          {filtered.length === 0 && <div className="text-muted small px-1">No matches</div>}
          {filtered.map((o) => (
            <div className="form-check" key={o.value}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`facet-${label}-${o.value}`}
                checked={sel.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              <label
                className="form-check-label small d-flex justify-content-between w-100"
                htmlFor={`facet-${label}-${o.value}`}
              >
                <span className="text-truncate me-2">{o.value}</span>
                <span className="badge text-bg-light">{o.count}</span>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
